import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
    UploadCloud, CheckCircle2, XCircle, AlertTriangle, Loader2, ShieldCheck,
    Info, FileSpreadsheet
} from 'lucide-react';
import api from '../lib/api';

const TypeBadge = ({ ok }) =>
    ok ? (
        <Badge className="bg-green-500/15 text-green-500 border-green-500/30">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
        </Badge>
    ) : (
        <Badge className="bg-destructive/15 text-destructive border-destructive/30">
            <XCircle className="w-3 h-3 mr-1" /> Issues
        </Badge>
    );

const STATUS_STYLES = {
    uploaded:   { label: 'Uploaded',   cls: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
    validating: { label: 'Validating', cls: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30' },
    processing: { label: 'Processing', cls: 'bg-primary/15 text-primary border-primary/30' },
    completed:  { label: 'Completed',  cls: 'bg-green-500/15 text-green-500 border-green-500/30' },
    failed:     { label: 'Failed',     cls: 'bg-destructive/15 text-destructive border-destructive/30' },
};

const JobProgressCard = ({ job }) => {
    const style = STATUS_STYLES[job.status] || STATUS_STYLES.uploaded;
    const pct = Number(job.progress_percent || 0);
    return (
        <Card className="border-primary/30 bg-primary/5" data-testid="import-job-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-primary" />
                        <span className="font-mono font-medium text-sm" data-testid="job-filename">{job.file_name}</span>
                    </div>
                    <Badge className={style.cls} data-testid="job-status">
                        {(job.status === 'processing' || job.status === 'validating' || job.status === 'uploaded') && (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {style.label}
                    </Badge>
                </div>

                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progress</span>
                        <span data-testid="job-progress-percent">{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all duration-300 ease-out"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-md bg-background/40 p-2">
                        <div className="text-muted-foreground">Total</div>
                        <div className="font-mono font-semibold" data-testid="job-total-rows">{(job.total_rows || 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-md bg-background/40 p-2">
                        <div className="text-muted-foreground">Processed</div>
                        <div className="font-mono font-semibold text-green-500" data-testid="job-processed-rows">{(job.processed_rows || 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-md bg-background/40 p-2">
                        <div className="text-muted-foreground">Failed</div>
                        <div className={`font-mono font-semibold ${job.failed_rows ? 'text-destructive' : ''}`} data-testid="job-failed-rows">{(job.failed_rows || 0).toLocaleString()}</div>
                    </div>
                </div>

                {job.status === 'failed' && job.error_message && (
                    <div className="text-xs text-destructive flex items-start gap-2" data-testid="job-error">
                        <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        {job.error_message}
                    </div>
                )}
                {job.status === 'completed' && (
                    <div className="text-xs text-green-500 flex items-center gap-2" data-testid="job-success">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Imported {(job.processed_rows || 0).toLocaleString()} rows successfully
                        {job.reporting_month ? ` · reporting month ${job.reporting_month}` : ''}.
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const SchemaList = ({ schema }) => (
    <Card className="bg-muted/20">
        <CardContent className="p-4 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">Required columns</div>
            <ul className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {schema.filter(s => s.required).map(s => (
                    <li key={s.name} className="flex items-baseline gap-1">
                        <span className="text-destructive font-bold">*</span>
                        <span className="font-medium">{s.name}</span>
                        <span className="text-muted-foreground"> — {s.hint}</span>
                    </li>
                ))}
            </ul>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mt-3 mb-1">Optional columns</div>
            <ul className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                {schema.filter(s => !s.required).map(s => (
                    <li key={s.name}>{s.name}</li>
                ))}
            </ul>
        </CardContent>
    </Card>
);

const SheetReport = ({ sheet }) => (
    <Card className={sheet.ok ? "border-green-500/30 bg-green-500/5" : "border-destructive/30 bg-destructive/5"}>
        <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-primary" />
                    <span className="font-mono font-semibold">{sheet.sheet}</span>
                    <Badge variant="outline" className="text-xs">{sheet.rows} rows</Badge>
                </div>
                <TypeBadge ok={sheet.ok} />
            </div>

            {sheet.fatal && (
                <div className="text-sm text-destructive flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    {sheet.fatal}
                </div>
            )}

            {sheet.missing_required_columns?.length > 0 && (
                <div className="text-sm">
                    <div className="text-destructive font-medium mb-1 flex items-center gap-1">
                        <XCircle className="w-3.5 h-3.5" /> Missing required columns
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {sheet.missing_required_columns.map(c => (
                            <Badge key={c} variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                                * {c}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {sheet.row_issues?.length > 0 && (
                <div className="text-sm">
                    <div className="text-yellow-500 font-medium mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Blank cells in required fields
                    </div>
                    <ul className="text-xs space-y-0.5">
                        {sheet.row_issues.map((i, idx) => (
                            <li key={idx} className="text-muted-foreground">
                                <span className="font-medium text-foreground">{i.column}</span>: {i.rows_affected} row(s)
                                {i.sample_excel_rows?.length > 0 && (
                                    <> — first at Excel row {i.sample_excel_rows.join(', ')}</>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {sheet.unknown_labels_count > 0 && (
                <div className="text-sm">
                    <div className="text-yellow-500 font-medium mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Unknown labels (will be auto-created)
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {sheet.unknown_labels.slice(0, 10).map(l => (
                            <Badge key={l} variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30 text-xs">
                                {l}
                            </Badge>
                        ))}
                        {sheet.unknown_labels_count > 10 && (
                            <Badge variant="outline" className="text-xs">+{sheet.unknown_labels_count - 10} more</Badge>
                        )}
                    </div>
                </div>
            )}

            {sheet.potential_duplicate_rows?.count > 0 && (
                <div className="text-xs text-muted-foreground flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500" />
                    {sheet.potential_duplicate_rows.count} potentially duplicate row(s) detected (same ISRC + Month + Country + Platform).
                </div>
            )}
        </CardContent>
    </Card>
);

/**
 * Excel pre-flight upload modal.
 * Props:
 *   - kind: 'revenue' | 'catalogue'
 *   - validateUrl: '/revenue/validate' | '/songs/validate'
 *   - uploadUrl:   '/revenue/upload'   | '/songs/upload'
 *   - pollUrlBase: when set (e.g. '/revenue/import-jobs'), upload responses are
 *                  expected to return a `{job_id}` and the modal will poll
 *                  `${pollUrlBase}/${job_id}` until completed/failed, displaying
 *                  row-level progress (recommended for large revenue files).
 *   - open / onOpenChange
 *   - onUploaded(response | finalJob) — called after a successful upload (or
 *                  successful job completion). For job mode, receives the final
 *                  job document.
 */
const ExcelUploadModal = ({ kind, validateUrl, uploadUrl, pollUrlBase, open, onOpenChange, onUploaded }) => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [validating, setValidating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [report, setReport] = useState(null);
    const [errorInfo, setErrorInfo] = useState(null);
    const [job, setJob] = useState(null);
    const [duplicateInfo, setDuplicateInfo] = useState(null);  // 409 payload — shown to user, then they can override
    const inputRef = useRef(null);
    const pollTimer = useRef(null);
    const acceptCsv = kind === 'revenue';
    const acceptStr = acceptCsv ? '.xlsx,.xls,.csv' : '.xlsx,.xls';
    const allowedRe = acceptCsv ? /\.(xlsx?|csv)$/i : /\.xlsx?$/i;

    const stopPolling = () => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    };

    const reset = () => {
        stopPolling();
        setFile(null);
        setReport(null);
        setErrorInfo(null);
        setJob(null);
        setDuplicateInfo(null);
    };

    useEffect(() => () => stopPolling(), []);

    // Build a verbose error object from any axios / network failure so the user
    // sees the *real* reason instead of a generic "Validation failed" toast.
    const describeError = (err, fallback) => {
        const r = err?.response;
        const status = r?.status;
        const data = r?.data;
        let detail = '';
        if (data) {
            if (typeof data === 'string') detail = data;
            else if (data.detail) detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
            else if (data.message) detail = data.message;
            else detail = JSON.stringify(data).slice(0, 400);
        }
        if (!detail && err?.message) detail = err.message;
        if (!detail) detail = fallback;

        let hint = '';
        if (status === 413) {
            hint = 'The selected file exceeds the server upload limit. Please contact your administrator or use a smaller file.';
        }
        else if (status === 401) hint = 'Your session has expired — please log in again.';
        else if (status === 403) hint = 'You do not have permission to upload here. Switch to an Admin or Founder account.';
        else if (status === 504 || status === 502) hint = 'The server took too long to respond. Try a smaller file or retry.';
        else if (!status && err?.message) hint = 'Network or proxy error — check your connection and retry.';
        return { status, detail, hint, fallback };
    };

    const handleFile = (f) => {
        if (!f) return;
        if (!allowedRe.test(f.name)) {
            toast.error(acceptCsv ? 'Please select an .xlsx, .xls, or .csv file' : 'Please select an .xlsx or .xls file');
            return;
        }
        setFile(f);
        setReport(null);
        setErrorInfo(null);
        setJob(null);
    };

    const runValidation = async () => {
        if (!file) return;
        setValidating(true);
        setErrorInfo(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await api.post(validateUrl, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setReport(res.data);
        } catch (err) {
            const info = describeError(err, 'Validation failed');
            setErrorInfo(info);
            toast.error(info.detail || 'Validation failed');
        } finally {
            setValidating(false);
        }
    };

    const pollJob = (jobId) => {
        stopPolling();
        pollTimer.current = setInterval(async () => {
            try {
                const res = await api.get(`${pollUrlBase}/${jobId}`);
                const j = res.data;
                setJob(j);
                if (j.status === 'completed' || j.status === 'failed') {
                    stopPolling();
                    if (j.status === 'completed') {
                        toast.success(`Imported ${(j.processed_rows || 0).toLocaleString()} rows`);
                        onUploaded?.(j);
                    } else {
                        toast.error(
    kind === 'revenue'
        ? "Revenue import couldn't be completed."
        : (j.error_message || 'Import failed')
);
                    }
                }
            } catch (err) {
                stopPolling();
                toast.error('Lost connection to import job');
            }
        }, 1500);
    };

    const runUpload = async (override = false) => {
        if (!file) return;
        setUploading(true);
        setErrorInfo(null);
        if (!override) setDuplicateInfo(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const url = override ? `${uploadUrl}?override_duplicate=true` : uploadUrl;
            const res = await api.post(url, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Job mode (large-file safe): backend returned a job_id; poll progress.
            if (pollUrlBase && res.data?.job_id) {
                setDuplicateInfo(null);
                setJob({
                    id: res.data.job_id,
                    file_name: res.data.file_name || file.name,
                    status: res.data.status || 'uploaded',
                    total_rows: 0,
                    processed_rows: 0,
                    failed_rows: 0,
                    progress_percent: 0,
                });
                pollJob(res.data.job_id);
                toast.success(override ? 'Duplicate override accepted — processing in background' : 'Upload accepted — processing in background');
                return;
            }

            // Sync mode (small files / songs): finished in one round-trip.
            toast.success('Upload complete');
            onUploaded?.(res.data);
            onOpenChange(false);
            reset();
        } catch (err) {
            // Duplicate upload (409) — surface the duplicate panel, NOT an error toast
            if (err?.response?.status === 409 && err.response.data?.detail?.code === 'duplicate_upload') {
                setDuplicateInfo(err.response.data.detail);
                return;
            }
            const info = describeError(err, 'Upload failed');
            setErrorInfo(info);
            toast.error(info.detail || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const isReady = report && report.validation_passed && !report.fatal;
    const jobActive = job && (job.status === 'uploaded' || job.status === 'validating' || job.status === 'processing');
    const jobDone = job && (job.status === 'completed' || job.status === 'failed');

    const handleDialogChange = (v) => {
        // Don't let the user accidentally close while a job is running
        if (!v && jobActive) {
            toast.message('Import is still running — please wait for it to finish.');
            return;
        }
        onOpenChange(v);
        if (!v) reset();
    };

    return (
        <Dialog open={open} onOpenChange={handleDialogChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`excel-upload-modal-${kind}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        {kind === 'revenue' ? 'Upload Revenue Excel' : 'Upload Catalogue Excel'}
                    </DialogTitle>
                    <DialogDescription>
                        Pre-flight check runs first. <span className="text-destructive font-bold">*</span> = required column.
                        {acceptCsv && ' Large files are processed in the background — feel free to keep this dialog open while it runs.'}
                    </DialogDescription>
                </DialogHeader>

                {/* File picker (hidden once a job is live) */}
                {!job && (
                    <div className="space-y-3">
                        <div
                            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                                file ? 'border-primary/40 bg-primary/5' : 'border-border/50 hover:border-primary/40'
                            }`}
                            onClick={() => inputRef.current?.click()}
                            data-testid="dropzone"
                        >
                            <UploadCloud className={`w-8 h-8 mx-auto mb-2 ${file ? 'text-primary' : 'text-muted-foreground'}`} />
                            <p className="text-sm">
                                {file
                                    ? (
                                        <>
                                            <span className="font-mono font-medium">{file.name}</span>
                                            {' · '}
                                            {file.size >= 1024 * 1024
                                                ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                                                : `${(file.size / 1024).toFixed(1)} KB`}
                                        </>
                                    )
                                    : `Click to pick an ${acceptCsv ? '.xlsx / .csv' : '.xlsx'} file`}
                            </p>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={acceptStr}
                                className="hidden"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                                data-testid="file-input"
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-2 justify-end">
                            {file && !report && (
                                <Button onClick={runValidation} disabled={validating} data-testid="btn-validate">
                                    {validating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validating</> : <>Run pre-flight check</>}
                                </Button>
                            )}
                            {report && !isReady && (
                                <Button onClick={() => { setReport(null); }} variant="outline" data-testid="btn-revalidate">
                                    Re-validate after fixing
                                </Button>
                            )}
                            {report && isReady && (
                                <Button onClick={() => runUpload(false)} disabled={uploading} className="bg-primary" data-testid="btn-upload">
                                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading</> : <>Confirm & Upload</>}
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Duplicate upload warning — user can override and upload anyway */}
                {duplicateInfo && (
                    <Card className="border-amber-500/40 bg-amber-500/5" data-testid="duplicate-warning">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-start gap-2 text-sm">
                                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
                                <div className="space-y-2 min-w-0 flex-1">
                                    <div className="font-medium text-amber-600">
                                        Duplicate file detected
                                    </div>
                                    <div className="text-xs text-muted-foreground space-y-1">
                                        {duplicateInfo.hash_match && (
                                            <div>
                                                <span className="text-foreground/80">Identical file already uploaded:</span>{' '}
                                                <span className="font-mono">{duplicateInfo.hash_match.filename}</span>{' '}
                                                {duplicateInfo.hash_match.uploaded_at && (
                                                    <span className="text-muted-foreground/70">({new Date(duplicateInfo.hash_match.uploaded_at).toLocaleString()})</span>
                                                )}
                                            </div>
                                        )}
                                        {duplicateInfo.dsp_month_match && (!duplicateInfo.hash_match || duplicateInfo.dsp_month_match.id !== duplicateInfo.hash_match.id) && (
                                            <div>
                                                <span className="text-foreground/80">Another file for the same DSP &amp; reporting month exists:</span>{' '}
                                                <span className="font-mono">{duplicateInfo.dsp_month_match.filename}</span>{' '}
                                                <span className="text-muted-foreground/70">
                                                    ({duplicateInfo.dsp} · {duplicateInfo.reporting_month})
                                                </span>
                                            </div>
                                        )}
                                        {duplicateInfo.filename_match && !duplicateInfo.hash_match && !duplicateInfo.dsp_month_match && (
                                            <div>
                                                <span className="text-foreground/80">A file with the same name already exists:</span>{' '}
                                                <span className="font-mono">{duplicateInfo.filename_match.filename}</span>{' '}
                                                {duplicateInfo.filename_match.uploaded_at && (
                                                    <span className="text-muted-foreground/70">({new Date(duplicateInfo.filename_match.uploaded_at).toLocaleString()})</span>
                                                )}
                                            </div>
                                        )}
                                        <div className="text-foreground/60">
                                            SHA256: <span className="font-mono">{(duplicateInfo.file_hash || '').slice(0, 16)}…</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => { setDuplicateInfo(null); }} data-testid="duplicate-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => runUpload(true)}
                                    disabled={uploading}
                                    className="bg-amber-500 hover:bg-amber-600 text-black"
                                    data-testid="duplicate-override-btn"
                                >
                                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Overriding</> : <>Upload Anyway</>}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Verbose error panel (shown when validate / upload failed at HTTP level) */}
                {errorInfo && (
                    <Card className="border-destructive/30 bg-destructive/5" data-testid="modal-error">
                        <CardContent className="p-4 space-y-2">
                            <div className="flex items-start gap-2 text-sm text-destructive">
                                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <div className="space-y-1 min-w-0">
                                    <div className="font-medium" data-testid="modal-error-title">
                                        {errorInfo.fallback}
                                        {errorInfo.status ? ` · HTTP ${errorInfo.status}` : ''}
                                    </div>
                                    <div className="text-muted-foreground text-xs whitespace-pre-wrap break-words" data-testid="modal-error-detail">
                                        {errorInfo.detail || 'No additional information was returned by the server.'}
                                    </div>
                                    {errorInfo.hint && (
                                        <div className="text-xs text-yellow-600 flex items-start gap-1">
                                            <Info className="w-3 h-3 mt-0.5 shrink-0" />
                                            {errorInfo.hint}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button size="sm" variant="outline" onClick={() => setErrorInfo(null)} data-testid="modal-error-dismiss">
                                    Dismiss
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Job progress (visible once upload is dispatched) */}
                {job && <JobProgressCard job={job} />}

                {/* Done — let user dismiss / start another */}
                {jobDone && (
    <div className="flex justify-end gap-2">
        {job?.status === 'failed' &&
            kind === 'revenue' &&
            job?.missing_labels?.length > 0 && (
                <Button
                    variant="secondary"
                    onClick={() => {
                        onOpenChange(false);
                        reset();
                        navigate('/admin/labels');
                    }}
                    data-testid="btn-open-labels"
                >
                    Open Labels
                </Button>
            )}

        <Button
            variant="outline"
            onClick={() => reset()}
            data-testid="btn-upload-another"
        >
            Upload another
        </Button>

        <Button
            onClick={() => {
                onOpenChange(false);
                reset();
            }}
            className="bg-primary"
            data-testid="btn-close"
        >
            Close
        </Button>
    </div>
)}

                {/* Validation report or schema list */}
                {!job && (
                    report ? (
                        <div className="space-y-3" data-testid="validation-report">
                            {report.fatal && (
                                <Card className="border-destructive/30 bg-destructive/5">
                                    <CardContent className="p-4 text-sm text-destructive flex items-start gap-2">
                                        <XCircle className="w-4 h-4 mt-0.5 shrink-0" /> {report.fatal}
                                    </CardContent>
                                </Card>
                            )}

                            <Card className={report.validation_passed ? "border-green-500/30 bg-green-500/5" : "border-destructive/30 bg-destructive/5"}>
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FileSpreadsheet className="w-4 h-4 text-primary shrink-0" />
                                            <span className="font-mono font-semibold text-sm truncate" data-testid="report-filename">
                                                {report.file_name || file?.name}
                                            </span>
                                        </div>
                                        {report.validation_passed ? (
                                            <Badge className="bg-green-500/15 text-green-500 border-green-500/30" data-testid="report-status">
                                                <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-destructive/15 text-destructive border-destructive/30" data-testid="report-status">
                                                <XCircle className="w-3 h-3 mr-1" /> Issues
                                            </Badge>
                                        )}
                                    </div>

                                    {report.missing_required_columns?.length > 0 && (
                                        <div className="text-sm" data-testid="missing-columns">
                                            <div className="text-destructive font-medium mb-1 flex items-center gap-1">
                                                <XCircle className="w-3.5 h-3.5" /> Missing required columns
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {report.missing_required_columns.map(c => (
                                                    <Badge key={c} variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                                                        * {c}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {report.detected_columns?.length > 0 && (
                                        <div className="text-sm" data-testid="detected-columns">
                                            <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">
                                                Detected columns ({report.detected_columns.length})
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {report.detected_columns.map(c => (
                                                    <Badge key={c} variant="outline" className="text-xs">
                                                        {c}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {report.validation_passed && (
                                        <div className="text-xs text-green-500 flex items-center gap-1">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            All mandatory columns are present. Confirm to start the import.
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    ) : (
                        <SchemaList schema={kind === 'revenue' ? REVENUE_SCHEMA_DISPLAY : CATALOGUE_SCHEMA_DISPLAY} />
                    )
                )}
            </DialogContent>
        </Dialog>
    );
};

// Lightweight schema for empty-state display (matches backend canonical names)
const REVENUE_SCHEMA_DISPLAY = [
    { name: "Product Title", required: true, hint: "Track / song name" },
    { name: "Product Artist Name", required: true, hint: "Lead artist" },
    { name: "ISRC", required: true, hint: "International Standard Recording Code" },
    { name: "Label Name", required: true, hint: "Label that owns the track" },
    { name: "Global Customer Name", required: true, hint: "Streaming platform / DSP" },
    { name: "Country Name", required: true, hint: "Country of streams" },
    { name: "Sale Units", required: true, hint: "Streams / units count" },
    { name: "INR Rev", required: true, hint: "Net INR revenue" },
    { name: "Transaction Month", required: true, hint: "Month of streams (YYYY-MM)" },
    { name: "Reporting Month", required: true, hint: "Month platform paid us (YYYY-MM)" },
    { name: "Net Value USD", required: false, hint: "Optional USD revenue" },
    { name: "Product Album Name", required: false },
    { name: "UPC", required: false },
    { name: "Country Code", required: false },
    { name: "Rev DSP", required: false }
];

const CATALOGUE_SCHEMA_DISPLAY = [
    { name: "Song Name", required: true, hint: "Track / product title" },
    { name: "Singer Name", required: true, hint: "Lead artist" },
    { name: "ISRC Code", required: true, hint: "Track ISRC" },
    { name: "Label Name", required: true, hint: "Label that owns the track" },
    { name: "UPC Code", required: false },
    { name: "Album Name", required: false },
    { name: "Composer", required: false },
    { name: "Lyricist", required: false },
    { name: "Release Date", required: false },
    { name: "Genre", required: false },
    { name: "Language", required: false }
];

export default ExcelUploadModal;
